import { useMemo, useState } from "react"
import { X, Store, CheckCircle2, Dumbbell, Building2, CarFront, Cpu, Package, Shirt, MapPin, LocateFixed, BriefcaseBusiness } from "lucide-react"
import { motion } from "framer-motion"
import { createStore } from "../../api/products/post"
import { getStoreLegacyType, STORE_CATEGORY_OPTIONS } from "../../utils/storeOptions"
import { Layers } from "lucide-react"
import BusinessLinePicker from "../../components/Onboarding/BusinessLinePicker"
import { BUSINESS_LINES } from "../../api/taxonomy"
import {
    DEFAULT_SERVICE_REVIEW_SOURCES,
    DEFAULT_SERVICE_REVIEW_ITEMS,
    DEFAULT_SERVICE_SHOWCASE_ITEMS,
    splitServiceHighlights,
} from "../../utils/serviceStorefront"

type Props = {
    isOpen: boolean
    onClose: () => void
    onAdd: (store: any) => void
}

export default function AddStoreModal({
    isOpen,
    onClose,
    onAdd,
}: Props) {
    const [name, setName] = useState("")
    const [city, setCity] = useState("")
    const [address, setAddress] = useState("")
    const [error, setError] = useState("")
    const [loading, setLoading] = useState(false)
    const [geoLoading, setGeoLoading] = useState(false)
    const [latitude, setLatitude] = useState("")
    const [longitude, setLongitude] = useState("")
    const [category, setCategory] = useState("GENERAL")
    const [businessLines, setBusinessLines] = useState<string[]>(["GENERAL_RETAIL"])
    const [serviceArea, setServiceArea] = useState("")
    const [websiteSummary, setWebsiteSummary] = useState("")
    const [serviceHighlights, setServiceHighlights] = useState("")
    const selectedCategory = useMemo(
        () => STORE_CATEGORY_OPTIONS.find((option) => option.key === category) || STORE_CATEGORY_OPTIONS[1],
        [category]
    )
    const isGymStore = category === "GYM"
    const isServiceStore = category === "SERVICE_BUSINESS"
    const isFashionStore = category === "FASHION"

    // Selecting "Mode" preselects the fashion lines so a clothing merchant is
    // never left staring at an empty picker.
    const handleCategoryChange = (next: string) => {
      setCategory(next)
      if (next === "FASHION" && businessLines.length === 1 && businessLines[0] === "GENERAL_RETAIL") {
        setBusinessLines(["FASHION_CLOTHING", "FASHION_SHOES", "FASHION_BAGS"])
      } else if (next !== "FASHION" && businessLines.some((l) => l.startsWith("FASHION") || l.startsWith("HOME"))) {
        setBusinessLines(["GENERAL_RETAIL"])
      }
    }

    const handleAutoGeocode = () => {
        if (typeof navigator === "undefined" || !navigator.geolocation) {
            setError("La géolocalisation n'est pas disponible sur cet appareil")
            return
        }

        setGeoLoading(true)
        setError("")

        navigator.geolocation.getCurrentPosition(
            (position) => {
                setLatitude(String(position.coords.latitude))
                setLongitude(String(position.coords.longitude))
                setGeoLoading(false)
            },
            () => {
                setGeoLoading(false)
                setError("Impossible de récupérer votre position. Autorisez la localisation puis réessayez.")
            },
            {
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 0,
            }
        )
    }

    const handleSubmit = async () => {
        if (!name || !city || !address) {
            setError("Veuillez remplir tous les champs obligatoires")
            return
        }

        if (isGymStore && (!latitude || !longitude)) {
            setError("Cliquez sur géocode automatique pour enregistrer la position du gym")
            return
        }

        try {
            setLoading(true)
            setError("")

            const newStore = await createStore({
                name,
                city,
                address,
                type: getStoreLegacyType(category),
                category,
                businessLines: businessLines.length ? businessLines : ["GENERAL_RETAIL"],
                isActive: isServiceStore ? false : undefined,
                latitude: latitude || undefined,
                longitude: longitude || undefined,
                serviceArea: serviceArea || undefined,
                websiteSummary: websiteSummary || undefined,
                serviceHighlights: splitServiceHighlights(serviceHighlights),
                showcaseMediaItems: isServiceStore ? DEFAULT_SERVICE_SHOWCASE_ITEMS : undefined,
                reviewSources: isServiceStore ? DEFAULT_SERVICE_REVIEW_SOURCES : undefined,
                reviewItems: isServiceStore ? DEFAULT_SERVICE_REVIEW_ITEMS : undefined,
            })

            // Update parent list
            onAdd(newStore)

            // Reset form
            setName("")
            setCity("")
            setAddress("")
            setLatitude("")
            setLongitude("")
            setServiceArea("")
            setWebsiteSummary("")
            setServiceHighlights("")
            setCategory("GENERAL")
            setBusinessLines(["GENERAL_RETAIL"])
            onClose()
        } catch (e: any) {
            setError(e.message || "Erreur lors de la création de la boutique")
        } finally {
            setLoading(false)
        }
    }

    if (!isOpen) return null

    return (
        <motion.section
            className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.25 }}
        >
            <div className="border-b border-slate-100 bg-gradient-to-r from-slate-50 via-white to-cyan-50 px-6 py-5">
                <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-50 text-blue-600">
                            <Store size={20} />
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold text-slate-800">
                                Nouvelle boutique
                            </h2>
                            <p className="mt-1 text-sm text-slate-500">
                                Ajoutez une boutique avec sa localisation et son mode de gestion.
                            </p>
                        </div>
                    </div>

                    <button onClick={onClose} className="rounded-lg p-1 hover:bg-slate-100">
                        <X />
                    </button>
                </div>
            </div>

            <div className="grid gap-6 px-6 py-6 lg:grid-cols-[minmax(0,1.2fr)_320px]">
                <div className="space-y-6">
                    {error && (
                        <div className="rounded-xl bg-red-50 p-3 text-sm text-red-600">
                            {error}
                        </div>
                    )}

                    <div className="grid gap-4 sm:grid-cols-2">
                        <div className="sm:col-span-2">
                            <label className="text-sm font-medium text-slate-700">
                                Nom de la boutique *
                            </label>
                            <input
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Ex: Iron Hub Plateau"
                                className="mt-1.5 w-full rounded-2xl border border-slate-200 px-4 py-3 text-slate-800 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-100"
                            />
                        </div>

                        <div>
                            <label className="text-sm font-medium text-slate-700">
                                Ville *
                            </label>
                            <input
                                value={city}
                                onChange={(e) => setCity(e.target.value)}
                                placeholder="Ex: Abidjan"
                                className="mt-1.5 w-full rounded-2xl border border-slate-200 px-4 py-3 text-slate-800 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-100"
                            />
                        </div>

                        <div>
                            <label className="text-sm font-medium text-slate-700">
                                Adresse *
                            </label>
                            <input
                                value={address}
                                onChange={(e) => setAddress(e.target.value)}
                                placeholder="Ex: Cocody Angre, 8e tranche"
                                className="mt-1.5 w-full rounded-2xl border border-slate-200 px-4 py-3 text-slate-800 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-100"
                            />
                        </div>
                    </div>

                    <div>
                        <div className="flex items-center justify-between gap-3">
                            <div>
                                <p className="text-sm font-medium text-slate-700">
                                    Categorie de boutique *
                                </p>
                                <p className="mt-1 text-xs text-slate-500">
                                    Choisissez le mode le plus adapte a l'activite de cette boutique.
                                </p>
                            </div>
                            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
                                {selectedCategory.label}
                            </span>
                        </div>

                        <div className="mt-4 grid gap-3 sm:grid-cols-2">
                            {STORE_CATEGORY_OPTIONS.map((option) => {
                                const active = category === option.key
                                return (
                                    <button
                                        key={option.key}
                                        type="button"
                                        onClick={() => handleCategoryChange(option.key)}
                                        className={`rounded-2xl border p-4 text-left transition ${
                                            active
                                                ? "border-blue-600 bg-blue-50 ring-2 ring-blue-100"
                                                : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                                        }`}
                                    >
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="flex items-start gap-3">
                                                <div className={`flex h-10 w-10 items-center justify-center rounded-2xl ${
                                                    active ? "bg-white text-blue-600" : "bg-slate-100 text-slate-600"
                                                }`}>
                                                    <CategoryIcon category={option.key} />
                                                </div>
                                                <div>
                                                    <p className="font-semibold text-slate-800">{option.label}</p>
                                                    <p className="mt-1 text-sm text-slate-500">{option.description}</p>
                                                </div>
                                            </div>
                                            {active && <CheckCircle2 className="mt-0.5 text-blue-600" size={18} />}
                                        </div>
                                    </button>
                                )
                            })}
                        </div>
                    </div>

                    {/* What this boutique actually sells. Drives which structured
                        product types (size / pointure / colour) they get later. */}
                    <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                            <div>
                                <p className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                                    <Layers className="text-cyan-600" size={16} />
                                    Que vendez-vous dans cette boutique ?
                                </p>
                                <p className="mt-1 text-xs text-slate-500">
                                    Sélectionnez tout ce qui s'applique. Une boutique peut vendre des
                                    vêtements, des chaussures et des sacs en même temps.
                                </p>
                            </div>
                            <span className="self-start rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
                                {businessLines.length} sélectionnée(s)
                            </span>
                        </div>

                        <div className="mt-4">
                            <BusinessLinePicker
                                value={businessLines}
                                onChange={setBusinessLines}
                                disabled={loading}
                            />
                        </div>

                        {businessLines.length === 0 && (
                            <p className="mt-3 rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-800">
                                Choisissez au moins une activité pour que GESTOCK sache quels produits
                                vous allez créer.
                            </p>
                        )}
                    </div>

                    {isGymStore && (
                        <div className="rounded-3xl border border-orange-200 bg-gradient-to-br from-orange-50 via-white to-amber-50 p-5">
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                                <div>
                                    <p className="text-sm font-semibold text-slate-800">
                                        Géocode du gym
                                    </p>
                                    <p className="mt-1 text-xs text-slate-500">
                                        Cliquez pour récupérer automatiquement la position GPS du gym depuis cet appareil.
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={handleAutoGeocode}
                                    disabled={geoLoading}
                                    className="inline-flex items-center gap-2 rounded-2xl bg-orange-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-orange-600 disabled:opacity-60"
                                >
                                    <LocateFixed size={16} />
                                    {geoLoading ? "Localisation..." : "Géocode automatique"}
                                </button>
                            </div>

                            <div className="mt-4 grid gap-4 sm:grid-cols-2">
                                <div>
                                    <label className="text-sm font-medium text-slate-700">
                                        Latitude
                                    </label>
                                    <input
                                        value={latitude}
                                        onChange={(e) => setLatitude(e.target.value)}
                                        placeholder="Ex: 5.348"
                                        className="mt-1.5 w-full rounded-2xl border border-slate-200 px-4 py-3 text-slate-800 outline-none transition focus:border-orange-300 focus:ring-4 focus:ring-orange-100"
                                    />
                                </div>

                                <div>
                                    <label className="text-sm font-medium text-slate-700">
                                        Longitude
                                    </label>
                                    <input
                                        value={longitude}
                                        onChange={(e) => setLongitude(e.target.value)}
                                        placeholder="Ex: -4.027"
                                        className="mt-1.5 w-full rounded-2xl border border-slate-200 px-4 py-3 text-slate-800 outline-none transition focus:border-orange-300 focus:ring-4 focus:ring-orange-100"
                                    />
                                </div>
                            </div>

                            <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-medium text-slate-600 ring-1 ring-orange-100">
                                <MapPin size={14} className="text-orange-500" />
                                {latitude && longitude ? `${latitude}, ${longitude}` : "Aucune coordonnée récupérée"}
                            </div>
                        </div>
                    )}

                    {isServiceStore && (
                        <div className="rounded-3xl border border-cyan-200 bg-gradient-to-br from-cyan-50 via-white to-sky-50 p-5">
                            <p className="text-sm font-semibold text-slate-800">
                                Parametres service
                            </p>
                            <p className="mt-1 text-xs text-slate-500">
                                Ces informations servent a la vitrine publique et a la connexion du site de services.
                            </p>

                            <div className="mt-4 grid gap-4">
                                <div>
                                    <label className="text-sm font-medium text-slate-700">
                                        Zone de service
                                    </label>
                                    <input
                                        value={serviceArea}
                                        onChange={(e) => setServiceArea(e.target.value)}
                                        placeholder="Ex: Abidjan, Cocody, Marcory et Plateau"
                                        className="mt-1.5 w-full rounded-2xl border border-slate-200 px-4 py-3 text-slate-800 outline-none transition focus:border-cyan-300 focus:ring-4 focus:ring-cyan-100"
                                    />
                                </div>

                                <div>
                                    <label className="text-sm font-medium text-slate-700">
                                        Resume vitrine
                                    </label>
                                    <textarea
                                        value={websiteSummary}
                                        onChange={(e) => setWebsiteSummary(e.target.value)}
                                        rows={4}
                                        placeholder="Ex: Prestations de nettoyage professionnel, devis rapides et interventions planifiees."
                                        className="mt-1.5 w-full rounded-2xl border border-slate-200 px-4 py-3 text-slate-800 outline-none transition focus:border-cyan-300 focus:ring-4 focus:ring-cyan-100"
                                    />
                                </div>

                                <div>
                                    <label className="text-sm font-medium text-slate-700">
                                        Points forts affiches
                                    </label>
                                    <input
                                        value={serviceHighlights}
                                        onChange={(e) => setServiceHighlights(e.target.value)}
                                        placeholder="Ex: Devis rapides, interventions 7j/7, equipe certifiee"
                                        className="mt-1.5 w-full rounded-2xl border border-slate-200 px-4 py-3 text-slate-800 outline-none transition focus:border-cyan-300 focus:ring-4 focus:ring-cyan-100"
                                    />
                                    <p className="mt-1 text-xs text-slate-500">
                                        Separez chaque point fort par une virgule pour alimenter la page services.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="space-y-4">
                    <div className="rounded-3xl border border-slate-200 bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 p-5 text-white">
                        <p className="text-xs uppercase tracking-[0.16em] text-white/60">Apercu</p>
                        <div className="mt-4 flex items-center gap-3">
                            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10">
                                <CategoryIcon category={selectedCategory.key} />
                            </div>
                            <div>
                                <p className="text-lg font-semibold">{name || "Nouvelle boutique"}</p>
                                <p className="text-sm text-white/70">{city || "Ville a definir"}</p>
                            </div>
                        </div>
                        <div className="mt-5 rounded-2xl bg-white/10 p-4">
                            <p className="text-sm font-medium">{selectedCategory.label}</p>
                            <p className="mt-1 text-sm text-white/75">{selectedCategory.description}</p>
                        </div>
                        <div className="mt-4 space-y-2 text-sm text-white/75">
                            <p>Adresse: {address || "A renseigner"}</p>
                            <p>Type backend: {getStoreLegacyType(category)}</p>
                            {isServiceStore && <p>Zone de service: {serviceArea || "A definir"}</p>}
                            {isServiceStore && <p>Highlights: {serviceHighlights || "A definir"}</p>}
                            {isGymStore && (
                                <p>Coordonnees: {latitude && longitude ? `${latitude}, ${longitude}` : "En attente"}</p>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex justify-end gap-3 border-t border-slate-100 px-6 py-4">
                <button
                    onClick={onClose}
                    className="rounded-xl border border-slate-200 px-4 py-2 hover:bg-slate-50"
                >
                    Annuler
                </button>

                <button
                    disabled={loading}
                    onClick={handleSubmit}
                    className="rounded-xl bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-50"
                >
                    {loading ? "Création..." : "Enregistrer"}
                </button>
            </div>
        </motion.section>
    )
}

function CategoryIcon({ category }: { category: string }) {
    if (category === "ELECTRONICS") return <Cpu size={18} />
    if (category === "GENERAL") return <Package size={18} />
    if (category === "SERVICE_BUSINESS") return <BriefcaseBusiness size={18} />
    if (category === "GYM") return <Dumbbell size={18} />
    if (category === "CAR_BOUTIQUE") return <CarFront size={18} />
    if (category === "REAL_ESTATE") return <Building2 size={18} />
    if (category === "FASHION") return <Shirt size={18} />
    return <Store size={18} />
}
